/**
 * SlideStage
 *
 * Centers a box sized to the slide image's aspect ratio inside its
 * parent, letterboxing the leftover space. Everything that has to line
 * up with the slide image — the `<img>`, the annotation canvas, the
 * laser dot — is rendered as an absolutely-positioned child of the
 * stage, so normalized 0..1 coordinates map to the SAME point on the
 * image in every window regardless of each window's shape.
 *
 * This is what makes the presenter window's pointer / pen line up with
 * the audience screen (#2153): both render the same `aspect` stage, so
 * a stroke captured at (0.5, 0.5) on the presenter's stage lands at the
 * image's center on the audience's stage too.
 *
 * The contained-box size is measured with a `ResizeObserver` (object-fit
 * math can't be read back from the DOM), so children get a concrete
 * pixel box to fill.
 */
import { useLayoutEffect, useRef, useState } from "react"

import { type Box, containedBox, DEFAULT_SLIDE_ASPECT } from "./slide-stage-utils"

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SlideStageProps {
  /** Slide image aspect ratio (width / height). Defaults to 16:9. */
  aspect?: number
  className?: string
  "data-testid"?: string
  /**
   * Children are positioned absolutely against the stage box (use
   * `absolute inset-0` to fill, or percentage offsets for overlays like
   * the laser dot).
   */
  children: React.ReactNode
}

export function SlideStage({
  aspect = DEFAULT_SLIDE_ASPECT,
  className,
  children,
  "data-testid": dataTestId,
}: SlideStageProps) {
  const parentRef = useRef<HTMLDivElement | null>(null)
  const [parentSize, setParentSize] = useState<Box>({ height: 0, width: 0 })

  useLayoutEffect(() => {
    const el = parentRef.current
    if (!el) return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      setParentSize((prev) =>
        prev.width === rect.width && prev.height === rect.height
          ? prev
          : { height: rect.height, width: rect.width }
      )
    }
    measure()
    if (typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const box = containedBox(parentSize.width, parentSize.height, aspect)

  return (
    <div
      ref={parentRef}
      className={`flex items-center justify-center overflow-hidden ${className ?? ""}`}
      data-testid={dataTestId}
    >
      <div className="relative" style={{ height: box.height, width: box.width }}>
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Laser dot (stage-relative)
// ---------------------------------------------------------------------------

export interface StageLaserDotProps {
  /** Normalized 0..1 position within the stage. */
  x: number
  y: number
}

/**
 * The red laser dot, positioned at a normalized point inside a
 * (relatively-positioned) SlideStage box. Used for the presenter's own
 * live feedback and for the mirrored dot on the audience screen.
 */
export function StageLaserDot({ x, y }: StageLaserDotProps) {
  return (
    <div
      aria-hidden="true"
      data-testid="presentation-laser-pointer"
      style={{
        backgroundColor: "rgb(255, 0, 0)",
        borderRadius: "9999px",
        boxShadow: "0 0 12px 4px rgba(255, 0, 0, 0.6)",
        height: 16,
        left: `${x * 100}%`,
        pointerEvents: "none",
        position: "absolute",
        top: `${y * 100}%`,
        transform: "translate(-50%, -50%)",
        width: 16,
        zIndex: 60,
      }}
    />
  )
}
